import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { selectPlan, selectShowCheckout, setDefaultPlanId } from '../../store/plans';
import { selectRole } from '../../store/info';
import styled, { css } from 'styled-components';
import {
	selectBrowserCoords,
	selectInitCoords,
	selectPhone,
} from '../../store/map';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { extraMapStyles } from '../extended/ApproximateLocation';
import Map from '../welcome/components/Map';
import { useEffect, useMemo } from 'react';
import FeaturesSlider from './components/FeaturesSlider';
import { AppButton } from '../../components';
import { WhiteCheckIcon } from './styled';
import ReviewSlider from './components/ReviewsSlider';
const isArabic = navigator.language.startsWith('ar');

const Wrapper = styled.section`
	margin-bottom: 40px;
`

const GetLocationText = styled.div`
	margin: 24px 0;
	text-align: center;
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font32};
	font-weight: ${(props) => props.theme.fontWeights.medium};
	letter-spacing: -0.16px;
	white-space: pre-line;
	> span {
		color: ${(props) => props.theme.colors.colorPrimary};
		font-weight: ${(props) => props.theme.fontWeights.bold};
		text-transform: capitalize;
	}
`;

export const CommonTitle = styled.h1`
	font-weight: ${(props) => props.theme.fontWeights.medium};
	font-size: ${(props) => props.theme.fontSizes.font24};
	color: ${(props) => props.theme.colors.colorText};
	text-align: center;
	margin: ${({ customMargin }) => customMargin || 0};
	white-space: pre-line;
	> span {
		color: ${(props) => props.theme.colors.colorPrimary};
	}
`;

const FeatureText = styled.p`
	color: ${(props) => props.theme.colors.colorText};
	font-size: ${(props) => props.theme.fontSizes.font16};
	font-weight: ${(props) => props.theme.fontWeights.extraLight};
	line-height: ${(props) => props.theme.lineHeights.lineHeight24};
	letter-spacing: -0.461px;
	text-align: left;
`;

export const StyledFeatureItem = styled.li`
	position: relative;
	font-weight: ${(props) => props.theme.fontWeights.extraLight};
	font-size: ${(props) => props.theme.fontSizes.font16};
	line-height: ${(props) => props.theme.lineHeights.lineHeight19};
	color: ${(props) => props.theme.colors.colorText};
	text-align: left;
	padding-left: 20px;
	&::before {
		content: '';
		position: absolute;
		left: 0;
		top: 3px;
		width: 12px;
		height: 12px;
		background-image: url('../assets/extended/checkIcon.svg');
		background-size: cover;
	}

	&:not(:last-child) {
		margin-bottom: 8px;
	}
`;

const StyledList = styled.ul`
	list-style: none;
	margin-top: 24px;
`;

export const MoneyBackWrapper = styled.div`
	max-width: 330px;
	width: 100%;
	height: 285px;
	background-image: url('../assets/extended/moneyBackBorder.png');
	background-size: 100% 100%;
	background-repeat: no-repeat;
	margin-top: ${({ btnAfter }) => (btnAfter ? '50px' : '0px')};
	padding: 0 19px 24px;
	position: relative;
	margin: 40px 0 12px;
`;

export const MoneyBackIcon = styled.div`
	width: 41px;
	height: 41px;
	background-image: url('../assets/extended/moneyBack.svg');
	position: absolute;
	left: 44%;
	top: -6%;
`;

export const MoneyBackTitle = styled.div`
	font-weight: ${(props) => props.theme.fontWeights.bold};
	font-size: ${(props) => props.theme.fontSizes.font24};
	line-height: ${(props) => props.theme.lineHeights.lineHeight31};
	color: ${(props) => props.theme.colors.colorPrimary};
	text-align: center;
	padding-top: 40px;
`;

export const MoneyBackDescription = styled.div`
	font-weight: ${(props) => props.theme.fontWeights.extraLight};
	font-size: ${(props) => props.theme.fontSizes.font14};
	line-height: ${(props) => props.theme.lineHeights.lineHeight24};
	color: ${(props) => props.theme.colors.colorGuaranteeText};
	margin-top: 10px;
	text-align: left;
	> span {
		text-decoration: underline;
	}
`;

export const IconsWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 28px;
`;

export const StyledIconImg = styled.img`
	min-width: ${({ imgWidth }) => `${imgWidth}px` || '75px'};
`;

const PlanList = styled.ul`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 0;
	margin: 24px 0 0 0;
	list-style: none;
`;

const PlanLi = styled.li`
	display: flex;
	flex-direction: ${isArabic ? 'row-reverse' : 'row'};
	justify-content: space-between;
	align-items: center;
	background: ${({ active, theme }) =>
		active ? theme.colors.colorWhite : theme.colors.colorInactivePlanBg};
	border: ${({ active, theme }) =>
		active
			? `1px solid ${theme.colors.colorPrimary}`
			: `1px solid transparent`};
	border-radius: 12px;
	width: 100%;
	height: 86px;
	position: relative;
	cursor: pointer;

	&:not(:last-child) {
		margin-bottom: 16px;
	}
`;

const StyledPeriod = styled.div`
	display: flex;
	flex-direction: ${isArabic ? 'row-reverse' : 'row'};
	justify-content: center;
	align-items: center;
	span {
		color: ${({ theme }) => theme.colors.colorText};
		font-weight: ${(props) => props.theme.fontWeights.bold};
		font-size: ${(props) => props.theme.fontSizes.font16};
		text-align: left;
		text-transform: capitalize;
	}
	div {
		display: flex;
		justify-content: center;
		width: 24px;
		height: 24px;
		min-width: 24px;
		border-radius: 50%;
		margin-right: 16px;
		margin-left: 16px;
		background: ${({ active, theme }) =>
			active ? theme.colors.colorActiveIconPlanBg : theme.colors.colorWhite};
		border: 1px solid
			${({ active, theme }) =>
				active
					? theme.colors.colorActiveIconPlanBg
					: theme.colors.colorInactiveIconBorder};
		transition: all 0.1s linear;
		transition-property: background-color, border-color;
		> svg {
			margin-top: 6px;
		}
	}
`;

const StyledPriceWrapper = styled.div`
	display: flex;
	align-items: center;
	margin-right: 16px;
`;

const OldPrice = styled.div`
	color: ${({ isDedicated, theme }) =>
		isDedicated
			? theme.colors.colorDedicatedOldPrice
			: theme.colors.colorOldPrice};
	font-weight: ${(props) => props.theme.fontWeights.bold};
	font-size: ${(props) => props.theme.fontSizes.font16};
	text-decoration: line-through;
	margin-right: 8px;
`;

const BestLabel = styled.div`
	position: absolute;
	height: 25px;
	top: 0;
	right: 32%;
	background: ${({ theme }) => theme.colors.colorPrimary};
	border-radius: 1000px;
	font-weight: ${(props) => props.theme.fontWeights.medium};
	font-size: ${(props) => props.theme.fontSizes.font11};
	color: ${(props) => props.theme.colors.colorWhite};
	text-transform: uppercase;
	transform: translateY(-50%);
	padding: 6px 14px;
`;

const GreenLabelBg = styled.div`
	background: url('../assets/extended/greenLabel.png');
	background-repeat: no-repeat;
	background-size: 100% 100%;
	min-width: 92px;
	width: 100%;
	height: 48px;
`;

const PriceWrapper = styled.div`
	display: flex;
	height: 100%;
	width: 100%;
	align-items: center;
	justify-content: center;
`;

const SmallItem = styled.div`
	color: ${(props) => props.theme.colors.colorWhite};
	font-size: ${(props) => props.theme.fontSizes.font16};
	font-weight: ${(props) => props.theme.fontWeights.bold};
	line-height: ${(props) => props.theme.lineHeights.lineHeight15};
	letter-spacing: -0.2px;
	${({ isDollar }) =>
		isDollar &&
		css`
			margin-right: 3px;
			height: 25px;
		`};
`;

const BigItem = styled(SmallItem)`
	font-size: ${(props) => props.theme.fontSizes.font32};
	line-height: normal;
`;

const SmallItemsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: flex-start;
	margin-left: 2px;
`;

const PerWeek = styled.div`
	color: ${(props) => props.theme.colors.colorWhite};
	font-size: ${(props) => props.theme.fontSizes.font8};
	font-weight: ${(props) => props.theme.fontWeights.medium};
`;

const MoneyBackText = styled.div`
	color: ${({ theme }) => theme.colors.colorText};
	font-weight: ${(props) => props.theme.fontWeights.light};
	font-size: ${(props) => props.theme.fontSizes.font14};
	text-align: center;
	text-transform: uppercase;
	text-decoration: underline;
	margin-top: 24px;
`

export const ICONS = [
	{
		id: 1,
		imgPath: '../assets/extended/visa.png',
		imgWidth: 77,
	},
	{
		id: 2,
		imgPath: '../assets/extended/mastercard.png',
		imgWidth: 34,
	},
	{
		id: 3,
		imgPath: '../assets/extended/paypal.png',
		imgWidth: 76,
	},
	{
		id: 4,
		imgPath: '../assets/extended/ssl_secure.png',
		imgWidth: 55,
	},
];

const SpecialPlans = ({ onContinueClick, handleSetPlan }) => {
	const { t } = useTranslation();
	const plan = useSelector(selectPlan);
	const dispatch = useDispatch();
	const role = useSelector(selectRole);
	const phoneValue = useSelector(selectPhone);
	const initCoords = useSelector(selectInitCoords);
	const { browserLat, browserLng } = useSelector(selectBrowserCoords);
	const showCheckout = useSelector(selectShowCheckout);
	const reviewsContent = useMemo(() => {
    return [
      {
				id: 1,
        reviewer: "Kevin Peters",
        text: t("review_1"),
      },
      {
				id: 2,
        reviewer: "John Lee",
        text: t("review_2"),
      },
      {
				id: 3,
        reviewer: "Megan Smith",
        text: t("review_3"),
      },
    ];
  }, [t]);
	const slidesContent = useMemo(() => {
		return [
			{
				id: 1,
				imgPath: '../assets/extended/real_time_location.png',
				title: t('real_time_location'),
				description: t('real_time_location_description'),
			},
			{
				id: 2,
				imgPath: '../assets/extended/place_alerts.png',
				title: t('place_alerts'),
				description: t('place_alerts_description'),
			},
			{
				id: 3,
				imgPath: '../assets/extended/find_loves.png',
				title: t('find_loves'),
				description: t('find_them'),
			},
			{
				id: 4,
				imgPath: '../assets/extended/share_flights.png',
				title: t('share_flights'),
				description: t('stay_in_touch'),
			},
			{
				id: 5,
				imgPath: '../assets/extended/auto_sos.png',
				title: t('auto_sos'),
				description: t('sos_alerts_description'),
			},
		];
	}, [t]);

	const defaultPaywall = useMemo(() => {
		return [
			{
				id: 'fl-monthly-3999',
				title: '1',
				period: t('month'),
				oldPrice: '$20.00',
				price: '9',
				week: `${t('per_week')}`,
				bestValue: true,
			},
			{
				id: 'fl-3month-5999',
				title: '3',
				period: t('months'),
				oldPrice: '$10.00',
				price: '4',
				week: t('per_week'),
			},
			{
				id: 'fl-6month-7176',
				title: '6',
				period: t('months'),
				oldPrice: '$6.00',
				price: '2',
				week: t('per_week'),
			},
		];
	}, [t]);
	useEffect(() => {
		if (!showCheckout) {
			handleSetPlan(defaultPaywall[0]?.id);
			dispatch(setDefaultPlanId(defaultPaywall[0]?.id));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, showCheckout]);
	const FEATURES = useMemo(() => {
		return [
			t('ensure_family_safety'),
			t('share_locations_instantly'),
			t('find_lost'),
			t('stay_linked'),
			t('share_flight'),
		];
	}, [t]);

	const renderItem = (i, index) => {
		return <StyledFeatureItem key={index}>{i}</StyledFeatureItem>;
	};
	const renderIcon = (item, index) => {
		return (
			<StyledIconImg
				src={item.imgPath}
				key={item.id || index}
				imgWidth={item.imgWidth}
			/>
		);
	};
	const renderPlans = (planInfo, index) => {
		const { id, title, period, oldPrice, price, week, bestValue, currencySymbol } = planInfo;
		const isActive = plan === id;
		return (
			<PlanLi
				active={isActive}
				onClick={() => handleSetPlan(id)}
				id={'plan-button-' + (index + 1)}
				key={id}
			>
				<StyledPeriod active={isActive}>
					<div>{WhiteCheckIcon}</div>
					<span>
						{title}-{period}
						<br /> {t('plan')}
					</span>
				</StyledPeriod>
				<StyledPriceWrapper>
					<OldPrice isDedicated={bestValue}>{oldPrice}</OldPrice>
					<GreenLabelBg>
						<PriceWrapper>
							<SmallItem isDollar>{currencySymbol || '$'}</SmallItem>
							<BigItem>{price}</BigItem>
							<SmallItemsWrapper>
								<SmallItem>99</SmallItem>
								<PerWeek>{week}</PerWeek>
							</SmallItemsWrapper>
						</PriceWrapper>
					</GreenLabelBg>
				</StyledPriceWrapper>
				{bestValue && <BestLabel>{t('most_popular')}</BestLabel>}
			</PlanLi>
		);
	};

	const continueButton = (displayStickyBtn, sticky, id) => (
		<AppButton
			customText={t('get_my_plan')}
			onClick={onContinueClick}
			bottomPosition={'16px'}
			displayStickyBtn={displayStickyBtn}
			position={sticky}
			displayStubBtn={false}
			customBeforeHeight={false}
			customId={`paywall-continue-button-${id}`}
		/>
	);
	
	return (
		<Wrapper>
			<GetLocationText
				dangerouslySetInnerHTML={{
					__html: t('get_real_location', {
						role: role,
						phoneNumber: formatPhoneNumberIntl(phoneValue),
					}),
				}}
			/>
			<Map
				lat={browserLat || initCoords.lat}
				lng={browserLng || initCoords.lng}
				customHeight={'250px'}
				customWidth={'330px'}
				customStyles={extraMapStyles}
				zoom={7}
			/>
			<CommonTitle customMargin={'40px 0 35px'}>
				{t('choose_your_plan')}
			</CommonTitle>
			<PlanList>{defaultPaywall.map(renderPlans)}</PlanList>
			<MoneyBackText>{t('30_money_back')}</MoneyBackText>
			{continueButton(false, 'fixed', 1)}
			<FeaturesSlider slideData={slidesContent} />
			<CommonTitle customMargin={'40px 0 8px'}>
				{t('what_you_get')}:
			</CommonTitle>
			<FeatureText>{t('our_technology')}</FeatureText>
			<StyledList>{FEATURES.map(renderItem)}</StyledList>
			<PlanList>{defaultPaywall.map(renderPlans)}</PlanList>
			<MoneyBackText>{t('30_money_back')}</MoneyBackText>
			{continueButton(false, 'fixed', 2)}
			<ReviewSlider slideData={reviewsContent} />
			<MoneyBackWrapper>
				<MoneyBackIcon />
				<MoneyBackTitle>{t('money_back_guarantee')}</MoneyBackTitle>
				<MoneyBackDescription
					dangerouslySetInnerHTML={{ __html: t('money_back_description') }}
				/>
			</MoneyBackWrapper>
			<CommonTitle
				customMargin={'40px 0 24px'}
				dangerouslySetInnerHTML={{ __html: t('safe_checkout') }}
			/>
			<IconsWrapper>{ICONS.map(renderIcon)}</IconsWrapper>
			{continueButton(false, 'fixed', 3)}
		</Wrapper>
	);
};

export default SpecialPlans;
